```vue
<template>
  <ModuleContainer v-bind="bindModule">
    <template #btn>
      <MapControlGroupButton row>
        <MapControlButton
          icon="mdi-pencil"
          :active="currentAction == 'update'"
          tooltip="Chọn để sửa thuộc tính"
          @click="currentAction = 'update'"
        />
        <MapControlButton
          icon="mdi-delete-outline"
          :active="currentAction == 'delete'"
          tooltip="Chọn để xóa"
          @click="currentAction = 'delete'"
        />
      </MapControlGroupButton>
    </template>
  </ModuleContainer>
</template>

<script>
import ModuleContainer from '@components/Map/ModuleContainer.vue'
import ModuleMixin from '@components/Map/mixins/ModuleMixin'
import MapControlButton from '@components/Map/control/MapControlButton.vue'
import MapControlGroupButton from '@components/Map/control/MapControlGroupButton.vue'

import { convertLinkToBackEnd } from '@/src/utils'
import { LngLatBounds } from 'mapbox-gl'

export default {
  components: {
    ModuleContainer,
    MapControlButton,
    MapControlGroupButton,
  },
  mixins: [ModuleMixin],
  props: {
    datastoreDetail: {
      required: true,
      type: Object,
    },
  },
  data: () => ({
    sourceId: null,
    layerIds: [],
    selectedFeature: null,
    hoveredFeatureId: null,
    saving: false,
    currentAction: 'update',
  }),
  computed: {
    tileUrl() {
      if (!this.datastoreDetail) {
        return null
      }
      if (!this.datastoreDetail.tile_link) {
        return null
      }
      return this.normalizeTileUrl(this.datastoreDetail.tile_link)
    },
    geometryType() {
      return this.datastoreDetail && this.datastoreDetail.meta
        ? this.datastoreDetail.meta.type
        : null
    },
  },
  watch: {
    datastoreDetail: {
      deep: true,
      handler() {
        this.removeLayers()
        this.loadLayer()
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.loadLayer()
    })
  },
  beforeDestroy() {
    this.removeLayers()
  },
  methods: {
    onInit() {
      this.loadLayer()
    },
    onDestroy() {
      this.removeLayers()
    },
    normalizeTileUrl(url) {
      if (!url) {
        return url
      }
      return convertLinkToBackEnd(url)
    },
    getSourceId() {
      return `datastore-detail-${this.datastoreDetail.id}`
    },
    getLayerId(type) {
      const id = this.datastoreDetail.id
      if (this.isPolygonType(type)) {
        return `datastore-detail-${id}-fill`
      }
      if (this.isLineType(type)) {
        return `datastore-detail-${id}-line`
      }
      return `datastore-detail-${id}-point`
    },
    getOutlineLayerId() {
      return `datastore-detail-${this.datastoreDetail.id}-outline`
    },
    isPointType(type) {
      return ['Point', 'MultiPoint'].includes(type)
    },
    isLineType(type) {
      return ['LineString', 'MultiLineString', 'Line'].includes(type)
    },
    isPolygonType(type) {
      return ['Polygon', 'MultiPolygon'].includes(type)
    },
    addSource(map) {
      const sourceId = this.getSourceId()

      this.sourceId = sourceId

      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }

      map.addSource(sourceId, {
        type: 'vector',
        tiles: [this.tileUrl],
        minzoom: 4,
        maxzoom: 14,
      })
    },
    addLayers(map) {
      const type = this.geometryType
      const sourceId = this.getSourceId()
      this.layerIds = []
      if (this.isPolygonType(type)) {
        const fillLayerId = this.getLayerId(type)
        const outlineLayerId = this.getOutlineLayerId()
        map.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          'source-layer': 'tile-server',
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#ff9800',
              ['boolean', ['feature-state', 'hover'], false],
              '#ffc107',
              '#3388ff',
            ],
            'fill-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0.65,
              ['boolean', ['feature-state', 'hover'], false],
              0.6,
              0.35,
            ],
          },
        })
        map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': 'tile-server',
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#ff5500',
              ['boolean', ['feature-state', 'hover'], false],
              '#ff9800',
              '#2255aa',
            ],

            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              3,
              ['boolean', ['feature-state', 'hover'], false],
              3,
              1,
            ],
          },
        })
        this.layerIds.push(fillLayerId, outlineLayerId)
        return
      }

      if (this.isLineType(type)) {
        const layerId = this.getLayerId(type)

        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          'source-layer': 'tile-server',
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#ff5500',
              ['boolean', ['feature-state', 'hover'], false],
              '#ffc107',
              '#3388ff',
            ],

            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              5,
              ['boolean', ['feature-state', 'hover'], false],
              4,
              2,
            ],
          },
        })
        this.layerIds.push(layerId)
        return
      }

      if (this.isPointType(type)) {
        const layerId = this.getLayerId(type)

        map.addLayer({
          id: layerId,
          type: 'circle',
          source: sourceId,
          'source-layer': 'tile-server',
          paint: {
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              9,
              ['boolean', ['feature-state', 'hover'], false],
              8,
              5,
            ],

            'circle-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#ff5500',
              ['boolean', ['feature-state', 'hover'], false],
              '#ffc107',
              '#3388ff',
            ],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
          },
        })

        this.layerIds.push(layerId)
      }
    },

    registerEvents(map) {
      const layers = this.layerIds

      layers.forEach((layerId) => {
        map.on('click', layerId, this.handleFeatureClick)
        map.on('mouseenter', layerId, this.handleMouseEnter)
        map.on('mouseleave', layerId, this.handleMouseLeave)
      })
    },

    unregisterEvents(map) {
      if (!map) {
        return
      }

      this.layerIds.forEach((layerId) => {
        map.off('click', layerId, this.handleFeatureClick)

        map.off('mouseenter', layerId, this.handleMouseEnter)

        map.off('mouseleave', layerId, this.handleMouseLeave)
      })
    },

    handleMouseEnter(e) {
      const map = this.map

      if (!map) {
        return
      }

      map.getCanvas().style.cursor = 'pointer'
    },

    handleMouseLeave(e) {
      const map = this.map

      if (!map) {
        return
      }

      map.getCanvas().style.cursor = ''
    },

    /**
     * Click vào feature.
     */
    handleFeatureClick(e) {
      if (!e.features || !e.features.length) {
        return
      }

      const feature = e.features[0]

      this.selectFeature(feature)
    },

    selectFeature(feature) {
      const map = this.map
      this.clearFeatureState()

      const featureId = this.getFeatureId(feature)
      this.selectedFeature = feature
      if (featureId !== undefined && featureId !== null) {
        const targetId =
          typeof featureId === 'string' &&
          featureId.trim() !== '' &&
          !isNaN(Number(featureId))
            ? Number(featureId)
            : featureId
        map.setFeatureState(
          {
            source: this.getSourceId(),
            sourceLayer: 'tile-server',
            id: targetId,
          },
          {
            selected: true,
          }
        )
        this.$emit('click:select', {
          feature: { ...feature, id: targetId },
          action: this.currentAction,
        })
      }
    },

    clearFeatureState() {
      const map = this.map

      if (!map || !this.selectedFeature) {
        return
      }

      const feature = this.selectedFeature
      if (feature.id !== undefined && feature.id !== null) {
        map.removeFeatureState({
          source: this.getSourceId(),
          sourceLayer: 'tile-server',
          id: feature.id,
        })
      }
    },
    clearSelectedFeature() {
      this.clearFeatureState()
      this.selectedFeature = null
    },
    getFeatureId(feature) {
      if (!feature) {
        return null
      }
      if (feature.id !== undefined && feature.id !== null) {
        return feature.id
      }
      if (feature.properties && feature.properties.id !== undefined) {
        return feature.properties.id
      }
      return null
    },
    async reloadSource() {
      const map = this.map
      if (!map || !this.sourceId) {
        return
      }
      const source = map.getSource(this.sourceId)
      if (source && source.reload) {
        source.reload()
        return
      }
      this.removeLayers()
      this.$nextTick(() => {
        this.loadLayer()
      })
    },
    loadLayer() {
      if (!this.datastoreDetail || !this.tileUrl || !this.geometryType) {
        return
      }
      const map = this.map
      this.removeLayers()
      this.addSource(map)
      this.addLayers(map)
      this.registerEvents(map)
    },
    setHoverFeature(id, geometry) {
      const map = this.map
      if (!map || !this.sourceId) {
        return
      }
      if (geometry) {
        this.flyToGeometry(geometry)
      }
      let targetId = id
      if (targetId && typeof targetId === 'object') {
        targetId =
          targetId.id !== undefined
            ? targetId.id
            : targetId.gid !== undefined
            ? targetId.gid
            : targetId.fid
      }
      if (
        typeof targetId === 'string' &&
        targetId.trim() !== '' &&
        !isNaN(Number(targetId))
      ) {
        targetId = Number(targetId)
      }

      if (
        this.hoveredFeatureId !== null &&
        this.hoveredFeatureId !== undefined
      ) {
        try {
          map.setFeatureState(
            {
              source: this.getSourceId(),
              sourceLayer: 'tile-server',
              id: this.hoveredFeatureId,
            },
            { hover: false }
          )
        } catch (e) {
          console.warn(e)
        }
      }
      this.hoveredFeatureId = targetId
      if (targetId !== null && targetId !== undefined) {
        try {
          map.setFeatureState(
            {
              source: this.getSourceId(),
              sourceLayer: 'tile-server',
              id: targetId,
            },
            { hover: true }
          )
        } catch (e) {
          console.warn(e)
        }
      }
    },
    flyToGeometry(geometry) {
      const map = this.map

      if (!map || !geometry) {
        return
      }

      try {
        const bounds = new LngLatBounds()

        const addCoordinates = (coordinates) => {
          if (!coordinates) return

          if (
            typeof coordinates[0] === 'number' &&
            typeof coordinates[1] === 'number'
          ) {
            bounds.extend(coordinates)
            return
          }

          coordinates.forEach(addCoordinates)
        }

        addCoordinates(geometry.coordinates)
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, {
            padding: 80,
            duration: 800,
            maxZoom: 17,
          })
        }
      } catch (e) {
        console.warn('Cannot fly to geometry:', e)
      }
    },
    removeLayers() {
      const map = this.map
      if (!map) {
        return
      }
      this.unregisterEvents(map)
      this.layerIds.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId)
        }
      })
      this.layerIds = []
      if (this.sourceId && map.getSource(this.sourceId)) {
        map.removeSource(this.sourceId)
      }
      this.sourceId = null
      this.clearFeatureState()
      this.setHoverFeature(null)
    },
    onReset() {
      this.reloadSource()
      this.clearFeatureState()
      this.setHoverFeature(null)
    },
  },
}
</script>
